/**
 * RTL & GUC rates by tier — `GET /admin/data/rtl-pricing/tier-rates`
 * (admin-rtl-pricing-config.md §"Rate by tier", owner 2026-10-08).
 *
 * The owner's sheet: one row per RTL product, its 12-month base rate (the LIVE
 * `/rtl-pricing` baseRates, what the sizer prices on), then the rate each
 * correspondent tier gets = base + that tier's spread from the tier table.
 * Tiers come from the response, sorted by spread — never hardcoded.
 *
 * The "Change" stepper is a WHAT-IF: it moves every tier spread by the same
 * amount on the 0.125% grid (the response's `shiftOptions`, ±2%) and the
 * shifted table renders under the current one. Nothing is saved from here —
 * a new spread is still set per tier in RTL & GUC Pricing → Tier spreads.
 *
 * Everything in the response is PERCENT (8.125 = 8.125%).
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { AgGridReact } from "ag-grid-react";

import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import AddRounded from "@mui/icons-material/AddRounded";
import RemoveRounded from "@mui/icons-material/RemoveRounded";
import RefreshRounded from "@mui/icons-material/Refresh";
import { useSnackbar } from "notistack";

import Flx from "../../../components/layout/Flx";
import Txt from "../../../components/typography/Txt";
import { adminRtlPricingApi } from "../../../api/adminDataApi";
import {
  MetaChip,
  PRICING_GRID_COL_DEF,
  PricingGrid,
  PricingSection,
  SectionEmpty,
  T,
} from "./pricingUi";

const pct = (v) =>
  v == null || Number.isNaN(Number(v)) ? "—" : `${Number(v).toFixed(3)}%`;
const signedPct = (v) =>
  `${Number(v) > 0 ? "+" : Number(v) < 0 ? "−" : "±"}${Math.abs(Number(v)).toFixed(3)}%`;

/** One rate-by-tier table. `tiers` = [[name, spreadPct], …] in spread order. */
const TierRateGrid = ({ rows, tiers }) => {
  const columnDefs = useMemo(
    () => [
      {
        headerName: "Product",
        field: "product",
        minWidth: 200,
        flex: 1.4,
        cellClass: "key",
      },
      {
        headerName: "Base rate (%)",
        field: "baseRatePct",
        minWidth: 130,
        flex: 1,
        cellClass: "num",
        headerClass: "num",
        valueFormatter: (p) => pct(p.value),
      },
      ...tiers.map(([name]) => ({
        headerName: name,
        colId: `tier-${name}`,
        minWidth: 130,
        flex: 1,
        cellClass: "num",
        headerClass: "num",
        valueGetter: (p) => p.data?.tiers?.[name]?.ratePct ?? null,
        valueFormatter: (p) => pct(p.value),
        tooltipValueGetter: (p) => {
          const s = p.data?.tiers?.[name]?.spreadPct;
          return s == null ? null : `${name} spread ${signedPct(s)}`;
        },
      })),
    ],
    [tiers],
  );

  return (
    <PricingGrid rowCount={rows.length} maxRows={10}>
      <AgGridReact
        rowData={rows}
        columnDefs={columnDefs}
        defaultColDef={PRICING_GRID_COL_DEF}
        getRowId={(p) => String(p.data.product)}
        tooltipShowDelay={300}
      />
    </PricingGrid>
  );
};

TierRateGrid.propTypes = {
  rows: PropTypes.array.isRequired,
  tiers: PropTypes.array.isRequired,
};

export default function RtlTierRatesPanel() {
  const { enqueueSnackbar } = useSnackbar();
  const [data, setData] = useState(null);
  const [shift, setShift] = useState(0);
  const [loading, setLoading] = useState(false);

  const load = useCallback(
    (shiftPct) => {
      setLoading(true);
      adminRtlPricingApi
        .tierRates(shiftPct)
        .then((d) => setData(d))
        .catch((err) =>
          enqueueSnackbar(
            err?.response?.data?.detail || "Couldn't load the rates by tier",
            { variant: "error" },
          ),
        )
        .finally(() => setLoading(false));
    },
    [enqueueSnackbar],
  );

  useEffect(() => {
    load(shift);
  }, [load, shift]);

  const tiers = useMemo(
    () =>
      Object.entries(data?.tiers || {}).sort((a, b) => Number(a[1]) - Number(b[1])),
    [data],
  );
  const options = useMemo(
    () => (data?.shiftOptions || []).map(Number).sort((a, b) => a - b),
    [data],
  );
  const idx = options.findIndex((o) => Math.abs(o - shift) < 1e-9);
  const step = (dir) => {
    const next = options[idx + dir];
    if (next != null) setShift(next);
  };

  const rows = data?.rows || [];
  const shifted = data?.shifted || [];

  return (
    <PricingSection
      id="cfg-tier-rates"
      title="RTL & GUC rates by tier"
      desc="The rate each correspondent tier gets on each product: the live 12-month base rate (what the sizer prices on) plus the tier's spread. Use Change to preview moving every tier by the same amount; it saves nothing — set a new spread per tier in RTL & GUC Pricing → Tier spreads."
      actions={
        <Tooltip title="Refresh">
          <span>
            <IconButton onClick={() => load(shift)} disabled={loading} size="small">
              <RefreshRounded fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      }
      dense
    >
      {data && !rows.length ? (
        <SectionEmpty>
          No base rates yet. Set them in RTL &amp; GUC Pricing → Base rates by
          product.
        </SectionEmpty>
      ) : (
        <Flx column g={0}>
          <TierRateGrid rows={rows} tiers={tiers} />

          {/* The what-if stepper, between the two tables like the owner's sheet. */}
          <Flx ac g={1} wrap sx={{ px: 2.5, py: 0.5 }}>
            <Txt sx={{ fontSize: 12.5, fontWeight: 600, color: T.ink }}>Change</Txt>
            <Flx
              ac
              sx={{
                border: `1px solid ${T.line}`,
                borderRadius: "8px",
                bgcolor: T.surface,
              }}
            >
              <IconButton
                size="small"
                aria-label="Lower every tier spread by one step"
                onClick={() => step(-1)}
                disabled={loading || idx <= 0}
              >
                <RemoveRounded fontSize="small" />
              </IconButton>
              <Txt
                sx={{
                  fontFamily: T.mono,
                  fontSize: 12.5,
                  fontWeight: 600,
                  minWidth: 78,
                  textAlign: "center",
                  color: shift === 0 ? T.muted : T.ink,
                }}
              >
                {signedPct(shift)}
              </Txt>
              <IconButton
                size="small"
                aria-label="Raise every tier spread by one step"
                onClick={() => step(1)}
                disabled={loading || idx < 0 || idx >= options.length - 1}
              >
                <AddRounded fontSize="small" />
              </IconButton>
            </Flx>
            <Txt sx={{ fontSize: 12, color: T.muted }}>
              {shift === 0
                ? `Steps of ${data?.stepPct ?? 0.125}% on every tier, up to ±${
                    options.length ? Math.max(...options.map(Math.abs)) : 2
                  }%.`
                : "Preview only — the table below shows the rates with every tier moved."}
            </Txt>
            {shift !== 0 ? <MetaChip tone="warn">what-if</MetaChip> : null}
          </Flx>

          {shift !== 0 && shifted.length ? (
            <TierRateGrid
              rows={shifted}
              tiers={tiers.map(([n, s]) => [n, Number(s) + shift])}
            />
          ) : null}
        </Flx>
      )}
    </PricingSection>
  );
}
